import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Guid } from 'guid-typescript';
import SignalRService from '../../services/SignalRService';
import { ErrorResponse, RejectedValue } from 'shared/interfaces/core-interfaces';
import { AuthHeader } from 'services/authHeader';
import { API_BASE_URL as baseUri } from 'config/env';

/**
 * Marks the caller's whole feed as seen when the user opens the bell.
 *
 * Takes no ids and reads no body. The per-id endpoint this replaced could only mark what the client
 * happened to hold - with a paged feed that clears the loaded page and leaves the badge counting
 * everything behind it - and it answered with the caller's entire notification history, which for a
 * long-lived account is megabytes of JSON per bell open.
 */
export const SeenNotifications = createAsyncThunk<void, void, { rejectValue: RejectedValue }>(
    'Slice/NotificationSlice/SeenNotifications',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/notification/seen-all', {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 204 || response.status === 200) {
            return;
        }
        else {
            return rejectWithValue({ message: 'Error in application', status: response.status });
        }
    }
);

/**
 * One page of the bell feed, as returned by `GET api/notification/paged`.
 *
 * `nextCursor` is opaque: the backend encodes the keyset it pages by into it, and the client's only
 * job is to hand it back verbatim. Building or parsing one here would be a 400 - see
 * `NotificationCursor` on the backend.
 */
export interface GetNotificationsPageResponse {
    items: Notification[];
    nextCursor: string | null;
    hasMore: boolean;
    /** Only sent for the first page (the request without a cursor); null on every follow-up page. */
    unseenCount: number | null;
}

/** Matches the backend's default; the endpoint rejects anything above 100. */
export const NOTIFICATION_PAGE_SIZE = 10;

/**
 * Fetches one page of the bell feed. Pass `null` for the first page, otherwise the `nextCursor`
 * of the previous response.
 */
export const GetNotificationsPage = createAsyncThunk<GetNotificationsPageResponse, string | null, { rejectValue: RejectedValue }>(
    'Slice/NotificationSlice/GetNotificationsPage',
    async (cursor: string | null, { rejectWithValue }) => {
        const query = cursor !== null
            ? `?limit=${NOTIFICATION_PAGE_SIZE}&cursor=${encodeURIComponent(cursor)}`
            : `?limit=${NOTIFICATION_PAGE_SIZE}`;

        const response = await fetch(baseUri + 'api/notification/paged' + query, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const pageResponse = await response.json() as GetNotificationsPageResponse;
            return pageResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export interface Notification {
    id: Guid;
    message: string;
    creatorId: Guid,
    creatorName: string,
    creationDate: Date,
    tenant?: string | null,
    notifyEmployeeId: Guid,
    notifyEmployeeName: string,
    tenantId: Guid,
    link: string,
    linkText: string,
    isSeen: boolean,

    /**
     * Optional heading, shown in the card's footer beside "Created by" rather than in the body.
     * Set by the producers whose notification is more than one sentence of plain text: WP5 puts
     * the automated message's subject here, WP1 the notification type's name. Sent in English and
     * translated through t(), like linkText.
     */
    subject?: string | null
}

export interface NotificationState {
    notifications: Notification[];
    unseen: number;
    signalRServiceInstance: SignalRService | null;
    statusCode: string,
    message: string,

    /** Opaque token for the next page, null once the whole feed is loaded. Never built here. */
    nextCursor: string | null,
    hasMore: boolean,
    /** Guards the scroll handler so one page is in flight at a time. */
    isLoadingPage: boolean,
}

const initialState: NotificationState = {
    notifications: [],
    unseen: 0,
    signalRServiceInstance: null,
    statusCode: '',
    message: '',
    nextCursor: null,
    hasMore: false,
    isLoadingPage: false,
};

const NotificationSlice = createSlice({
    name: 'NotificationSlice',
    initialState,
    reducers: {
        addNotification: (state, action: PayloadAction<Notification>) => {
            // Prepended, not appended: the feed is newest-first, so a live notification belongs at
            // the top. Appending would drop it below the oldest loaded page and make it jump to the
            // top on the next fetch.
            const isAlreadyLoaded = state.notifications.some(n => n.id.toString() === action.payload.id.toString());
            if (isAlreadyLoaded) {
                return;
            }

            state.notifications.unshift(action.payload);
            // Counted incrementally rather than over the array: with paging the array is one page of
            // the feed, so its unseen rows are not the user's unseen total.
            if (action.payload.isSeen === false) {
                state.unseen += 1;
            }
        },
        /**
         * The badge, straight from the hub. It is the only thing the hub says about the existing
         * feed - the notifications themselves are never pushed, only paged in on bell open - so it
         * is authoritative and simply overwrites whatever the badge showed.
         */
        setUnseenCount: (state, action: PayloadAction<number>) => {
            state.unseen = action.payload;
        },
        resetNotification: (state) => {
            state.notifications = [];
            state.nextCursor = null;
            state.hasMore = false;
        },
        setSignalRServerInstance: (state, action: PayloadAction<SignalRService>) => {
            state.signalRServiceInstance = action.payload;
        },
        removeSignalRServerInstance: (state) => {
            state.signalRServiceInstance = null;
        }
    },
    extraReducers(builder) {
        //SeenNotifications
        // The bell's refresh starts here, not at the page fetch that follows it: the list is
        // cleared as the menu opens, so without a loading flag this early the dropdown would show
        // "no notifications" for the length of this request.
        builder.addCase(SeenNotifications.pending, (state) => {
            state.isLoadingPage = true;
        })
            .addCase(SeenNotifications.rejected, (state, action) => {
                state.isLoadingPage = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(SeenNotifications.fulfilled, (state) => {
                state.statusCode = '200';
                state.message = '';

                // The server cleared the whole feed, so zero is truthful even though the client
                // only holds a page of it.
                state.notifications.forEach(notification => { notification.isSeen = true; });
                state.unseen = 0;
                // isLoadingPage deliberately stays true: the page fetch is chained onto this call
                // and its own pending would only set it again. Clearing it here would flash the
                // empty state between the two requests.
            })

            //GetNotificationsPage
            .addCase(GetNotificationsPage.pending, (state) => {
                state.isLoadingPage = true;
            })
            .addCase(GetNotificationsPage.fulfilled, (state, action) => {
                state.statusCode = '200';
                state.message = '';
                state.isLoadingPage = false;

                // The cursor the request was made with tells first page from follow-up page; the
                // response itself carries no such flag.
                const isFirstPage = action.meta.arg === null;

                if (isFirstPage) {
                    state.notifications = action.payload.items;
                }
                else {
                    // De-duplicated by id: a notification that arrived over the hub while the user
                    // was scrolling has already been prepended, and the keyset can still hand it
                    // back on a later page.
                    const loadedIds = new Set(state.notifications.map(n => n.id.toString()));
                    const newItems = action.payload.items.filter(item => !loadedIds.has(item.id.toString()));
                    state.notifications = state.notifications.concat(newItems);
                }

                state.nextCursor = action.payload.nextCursor;
                state.hasMore = action.payload.hasMore;

                // Sent on the first page only, so a follow-up page must not overwrite the badge
                // with its null.
                if (action.payload.unseenCount !== null && action.payload.unseenCount !== undefined) {
                    state.unseen = action.payload.unseenCount;
                }
            })
            .addCase(GetNotificationsPage.rejected, (state, action) => {
                state.isLoadingPage = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                // The cursor is left untouched so the user can retry the same page by scrolling
                // again; clearing it would silently end the feed at the failed page.
            })
    }
});

export const { addNotification, setUnseenCount, resetNotification, setSignalRServerInstance, removeSignalRServerInstance } = NotificationSlice.actions;
export default NotificationSlice.reducer;